<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useNotificationStore } from '../stores/notificationStore';

const showInstallPrompt = ref(false);
const deferredPrompt = ref<any>(null);
const notificationStore = useNotificationStore();
const isIOS = ref(false);
const isStandalone = ref(false);
const isInstallable = ref(false);
const isWindows = ref(false);

onMounted(() => {
  // Detectar plataforma
  isIOS.value = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
  isWindows.value = /Windows/.test(navigator.userAgent);
  
  // Detectar si está en modo standalone (ya instalada)
  isStandalone.value = window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone ||
    document.referrer.includes('android-app://');
  
  // Escuchar evento de instalación
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt.value = e;
    isInstallable.value = true;
    showInstallPrompt.value = true;
  });

  // Escuchar si la app ya está instalada
  window.addEventListener('appinstalled', () => {
    showInstallPrompt.value = false;
    deferredPrompt.value = null;
    isInstallable.value = false;
    notificationStore.success('¡Aplicación instalada correctamente!');
  });
});

const installApp = async () => {
  if (!deferredPrompt.value) return;
  
  try {
    deferredPrompt.value.prompt();
    const { outcome } = await deferredPrompt.value.userChoice;
    
    if (outcome === 'accepted') {
      notificationStore.success('Instalación iniciada');
    } else {
      notificationStore.info('Instalación cancelada');
    }
  } catch (error) {
    notificationStore.error('Error al instalar la aplicación');
  } finally {
    deferredPrompt.value = null;
    showInstallPrompt.value = false;
  }
};

const dismissPrompt = () => {
  showInstallPrompt.value = false;
};
</script>

<template>
  <div v-if="!isStandalone && (showInstallPrompt || isIOS)" 
       class="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-white p-6 rounded-lg shadow-lg z-50 max-w-md w-full mx-4">
    <div class="flex flex-col gap-4">
      <div>
        <h3 class="text-lg font-semibold text-slate-800">Instalar aplicación</h3>
        <p class="text-sm text-slate-600 mt-1">
          <template v-if="isIOS">
            Para instalar la app en iOS: Toca el botón compartir y selecciona "Agregar a pantalla de inicio"
          </template>
          <template v-else-if="isWindows && isInstallable">
            Instala la app para acceso rápido desde tu escritorio
          </template>
          <template v-else>
            Instala la app para acceso rápido y uso sin conexión
          </template>
        </p>
      </div>

      <div v-if="isIOS" class="flex items-center gap-2">
        <img src="/ios-share.png" alt="iOS Share Button" class="w-6 h-6" />
        <span class="text-sm text-slate-600">Toca este ícono en Safari</span>
      </div>

      <div class="flex flex-col sm:flex-row gap-2 justify-end">
        <button 
          @click="dismissPrompt"
          class="px-4 py-2 text-slate-600 hover:text-slate-800 text-sm"
        >
          Ahora no
        </button>
        <button 
          v-if="isInstallable"
          @click="installApp"
          class="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 text-sm"
        >
          Instalar
        </button>
      </div>
    </div>
  </div>
</template>